import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { OwnerLayout } from '../../components/layouts/OwnerLayout';
import { CreditCard, Check, Loader2, Users, Building, Briefcase, ExternalLink, Crown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const PLANS = [
  {
    id: 'autonomo',
    name: 'Autónomo',
    monthlyPrice: 29,
    annualPrice: 290,
    description: 'Para profesionales independientes',
    icon: Briefcase,
    features: [
      '1 usuario (tú eres jefe y trabajador)',
      'Hasta 50 clientes',
      'Partes de trabajo ilimitados',
      'Plantillas de reportes',
      'Generación de PDF',
      'Asistente IA',
      'Soporte por email'
    ],
    limits: { users: 1, clients: 50 }
  },
  {
    id: 'pyme',
    name: 'PYME',
    monthlyPrice: 39,
    annualPrice: 390,
    description: 'Para pequeñas y medianas empresas',
    icon: Users,
    popular: true,
    features: [
      'Hasta 4 usuarios',
      'Hasta 150 clientes',
      'Partes de trabajo ilimitados',
      'Plantillas de reportes',
      'Generación de PDF',
      'Asistente IA',
      'Rol de administrador',
      'Soporte prioritario'
    ],
    limits: { users: 4, clients: 150 }
  },
  {
    id: 'empresa',
    name: 'Empresa',
    monthlyPrice: 49,
    annualPrice: 490,
    description: 'Para grandes empresas',
    icon: Building,
    features: [
      'Usuarios ilimitados',
      'Clientes ilimitados',
      'Partes de trabajo ilimitados',
      'Plantillas de reportes',
      'Generación de PDF',
      'Asistente IA',
      'Rol de administrador',
      'Soporte premium 24/7',
      'Informes avanzados'
    ],
    limits: { users: null, clients: null }
  }
];

const Subscription = () => {
  const { api } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const res = await api.get('/subscription/current');
      setSubscription(res.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    setProcessing(planId);
    try {
      const res = await api.post('/subscription/create-checkout', {
        plan: planId,
        billing_period: billingPeriod,
        origin_url: window.location.origin
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al procesar el pago'));
      setProcessing(null);
    }
  };

  const handleManageSubscription = async () => {
    setProcessing('manage');
    try {
      const res = await api.post('/subscription/portal');
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al abrir el portal'));
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
        </div>
      </OwnerLayout>
    );
  }

  const isActive = subscription?.status === 'active' || subscription?.status === 'trialing';
  const currentPlan = subscription?.plan || 'trial';
  const isDemo = subscription?.is_demo || false;
  const canManage = subscription?.can_manage || false;
  const isTrialing = subscription?.status === 'trialing' || subscription?.is_trialing;
  const trialEndsAt = subscription?.trial_end ? new Date(subscription.trial_end * 1000) : null;
  const isPendingCheckout = currentPlan === 'trial' && !isActive; // User registered but hasn't completed checkout
  
  // Get the plan info for display
  const currentPlanInfo = PLANS.find(p => p.id === currentPlan);
  const displayPlanName = currentPlanInfo?.name || (currentPlan === 'trial' ? 'Sin plan' : currentPlan);

  return (
    <OwnerLayout>
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-900">
            {isPendingCheckout ? 'Elige tu plan para comenzar' : isActive ? 'Tu suscripción' : 'Elige tu plan'}
          </h1>
          <p className="mt-2 text-slate-600">
            {isPendingCheckout 
              ? 'Selecciona un plan para disfrutar de 14 días de prueba gratis.'
              : isActive 
                ? `Actualmente en el plan ${PLANS.find(p => p.id === currentPlan)?.name || 'Profesional'}`
                : '14 días de prueba gratis. Cancela cuando quieras.'
            }
          </p>
          
          {/* Billing toggle - show when not on an active plan or pending checkout */}
          {(!isActive || isPendingCheckout) && (
            <div className="mt-6 inline-flex items-center bg-slate-100 rounded-full p-1" data-testid="subscription-billing-toggle">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  billingPeriod === 'monthly'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="subscription-billing-monthly"
              >
                Mensual
              </button>
              <button
                onClick={() => setBillingPeriod('annual')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  billingPeriod === 'annual'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="subscription-billing-annual"
              >
                Anual
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">
                  -2 meses
                </span>
              </button>
            </div>
          )}
        </div>

        {/* Pending Checkout Warning */}
        {isPendingCheckout && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-amber-900">Completa tu registro</h3>
                  <p className="text-sm text-amber-700">
                    Elige un plan para comenzar tu prueba gratuita de 14 días. 
                    Durante la prueba tendrás acceso completo a todas las funcionalidades del plan elegido.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current Subscription Status */}
        {isActive && (
          <Card className="bg-gradient-to-r from-sky-50 to-blue-50 border-sky-200">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <Crown className="w-6 h-6 text-sky-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      Plan {PLANS.find(p => p.id === currentPlan)?.name || currentPlan || 'Prueba'}
                      {isDemo && <span className="text-xs ml-2 text-orange-600">(Demo)</span>}
                      {isTrialing && !isDemo && (
                        <Badge className="ml-2 bg-amber-100 text-amber-800">Período de prueba</Badge>
                      )}
                    </h3>
                    <p className="text-sm text-slate-600">
                      {isTrialing && trialEndsAt ? (
                        <>Tu prueba gratuita termina el {trialEndsAt.toLocaleDateString('es-ES')}. Después se cobrará automáticamente.</>
                      ) : subscription?.current_period_end ? (
                        <>Próxima facturación: {new Date(subscription.current_period_end).toLocaleDateString('es-ES')}</>
                      ) : isDemo ? (
                        'Cuenta de demostración'
                      ) : (
                        'Suscripción activa'
                      )}
                    </p>
                    {/* Show plan limits */}
                    {PLANS.find(p => p.id === currentPlan)?.limits && (
                      <div className="flex gap-4 mt-2 text-xs text-slate-500">
                        <span>
                          Usuarios: {PLANS.find(p => p.id === currentPlan)?.limits.users || 'Ilimitados'}
                        </span>
                        <span>
                          Clientes: {PLANS.find(p => p.id === currentPlan)?.limits.clients || 'Ilimitados'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                {canManage ? (
                  <Button 
                    variant="outline"
                    onClick={handleManageSubscription}
                    disabled={processing === 'manage'}
                    className="w-full sm:w-auto"
                  >
                    {processing === 'manage' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Gestionar suscripción
                      </>
                    )}
                  </Button>
                ) : isDemo ? (
                  <span className="text-sm text-slate-500 bg-slate-100 px-3 py-2 rounded-lg">
                    Gestión no disponible en demo
                  </span>
                ) : null}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {PLANS.map((plan) => {
            const Icon = plan.icon;
            const isCurrent = isActive && currentPlan === plan.id;
            const displayPrice = billingPeriod === 'annual' ? plan.annualPrice : plan.monthlyPrice;
            const monthlyEquivalent = billingPeriod === 'annual' ? (plan.annualPrice / 12).toFixed(2) : null;
            const savings = billingPeriod === 'annual' ? (plan.monthlyPrice * 12) - plan.annualPrice : 0;
            
            return (
              <Card 
                key={plan.id}
                className={`relative ${plan.popular ? 'border-sky-500 border-2 shadow-lg' : ''} ${isCurrent ? 'bg-sky-50' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-sky-500 text-white">Más popular</Badge>
                  </div>
                )}
                
                <CardHeader className="text-center pb-2">
                  <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center ${plan.popular ? 'bg-sky-100' : 'bg-slate-100'}`}>
                    <Icon className={`w-6 h-6 ${plan.popular ? 'text-sky-600' : 'text-slate-600'}`} />
                  </div>
                  <CardTitle className="mt-4">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                
                <CardContent className="text-center">
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-slate-900">{displayPrice}€</span>
                    <span className="text-slate-500">/{billingPeriod === 'annual' ? 'año' : 'mes'}</span>
                    {billingPeriod === 'annual' && (
                      <div className="mt-1">
                        <span className="text-sm text-slate-500">{monthlyEquivalent}€/mes</span>
                        <span className="text-xs text-green-600 font-medium ml-2">Ahorras {savings}€/año</span>
                      </div>
                    )}
                  </div>
                  
                  <ul className="space-y-3 mb-6 text-left">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {isCurrent ? (
                    <Button disabled className="w-full" variant="outline">
                      Plan actual
                    </Button>
                  ) : isActive && canManage ? (
                    <Button 
                      className="w-full"
                      variant={plan.popular ? 'default' : 'outline'}
                      onClick={handleManageSubscription}
                      disabled={processing !== null}
                    >
                      {processing === 'manage' ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Abriendo portal...
                        </>
                      ) : (
                        'Cambiar a este plan'
                      )}
                    </Button>
                  ) : isActive && isDemo ? (
                    <Button disabled className="w-full" variant="outline">
                      No disponible en demo
                    </Button>
                  ) : (
                    <Button 
                      className={`w-full ${plan.popular ? 'bg-sky-600 hover:bg-sky-700' : ''}`}
                      variant={plan.popular ? 'default' : 'outline'}
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={processing !== null}
                    >
                      {processing === plan.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Procesando...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Empezar prueba gratis
                        </>
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* FAQ or additional info */}
        <Card className="bg-slate-50">
          <CardContent className="p-6 text-center">
            <h3 className="font-semibold text-slate-900 mb-2">¿Tienes preguntas?</h3>
            <p className="text-sm text-slate-600">
              Todos los planes incluyen 14 días de prueba gratis. Puedes cancelar en cualquier momento.
              <br />
              El pago se procesa de forma segura con Stripe.
            </p>
          </CardContent>
        </Card>
      </div>
    </OwnerLayout>
  );
};

export default Subscription;
